/**
 * Phase F.6 Batch D — Admin Dashboard widgets.
 *   - AIUsageWidget        → daily scans + avg latency + items extracted
 *   - ComplianceAuditWidget → total users vs total signup acceptances
 *   - BulkSubscriberActions → mass suspend/reactivate/delete tenants
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkles, ShieldCheck, AlertTriangle, Users, Pause, Play, Trash2, Loader2 } from "lucide-react";
import { InfoBubble } from "./InfoBubble";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AIUsageWidget() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => {
    axios.get(`${API}/admin/widgets/ai-usage?days=${days}`).then(r => setData(r.data)).catch(() => setData({}));
  }, [days]);

  return (
    <Card data-testid="ai-usage-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-cyan-500" />
          AI Scanner Usage
          <InfoBubble
            title="AI Scanner Usage"
            body="Counts every Gemini Nano-Banana invocation across the platform. Useful for spotting abuse and billing premium tiers for high-volume AI."
            testid="ai-usage-info"
          />
        </CardTitle>
        <CardDescription className="flex items-center justify-between">
          <span>Inventory image-scan activity</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-7 w-24 text-xs" data-testid="ai-usage-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 days</SelectItem>
              <SelectItem value="30">30 days</SelectItem>
              <SelectItem value="90">90 days</SelectItem>
            </SelectContent>
          </Select>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="rounded border p-2"><p className="text-xs text-muted-foreground">Scans</p><p className="text-xl font-bold">{data.scans ?? 0}</p></div>
            <div className="rounded border p-2"><p className="text-xs text-muted-foreground">Items extracted</p><p className="text-xl font-bold">{data.items_extracted ?? 0}</p></div>
            <div className="rounded border p-2"><p className="text-xs text-muted-foreground">Avg latency</p><p className="text-xl font-bold">{data.avg_latency_ms ?? 0} ms</p></div>
            <div className="rounded border p-2"><p className="text-xs text-muted-foreground">Failures</p><p className="text-xl font-bold">{data.failures ?? 0}</p></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ComplianceAuditWidget() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/widgets/compliance`).then(r => setData(r.data)).catch(() => setData({}));
  }, []);
  const missing = data?.missing_count ?? 0;
  const okPct = data?.total_users ? Math.round(((data.total_users - missing) / data.total_users) * 100) : 100;

  return (
    <Card data-testid="compliance-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          Consent Audit
          <InfoBubble
            title="Compliance audit"
            body="Every user who signs up through the public form now has their Terms+Privacy acceptance logged with IP + version. This widget surfaces any user missing that audit row — typically only users seeded by SQL/scripts."
            testid="compliance-info"
          />
        </CardTitle>
        <CardDescription>Terms & Privacy acceptance trail</CardDescription>
      </CardHeader>
      <CardContent>
        {!data ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Users with audit row</span>
              <Badge variant={okPct === 100 ? "default" : "secondary"}>{okPct}%</Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="rounded border p-2"><p className="text-muted-foreground">Total users</p><p className="text-lg font-bold">{data.total_users}</p></div>
              <div className="rounded border p-2"><p className="text-muted-foreground">Signed</p><p className="text-lg font-bold text-emerald-600">{data.total_signed_acceptances}</p></div>
              <div className="rounded border p-2"><p className="text-muted-foreground">Missing</p><p className="text-lg font-bold text-amber-600">{missing}</p></div>
            </div>
            {missing > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {missing} user{missing !== 1 ? "s" : ""} missing audit
                </summary>
                <ul className="mt-1 space-y-0.5 pl-4">
                  {(data.missing_users_sample || []).map(u => <li key={u.id} className="text-muted-foreground">{u.email}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BulkSubscriberActions({ tenants = [], onChanged }) {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAll = () => setSelected(new Set(tenants.map(t => t.id)));
  const clearAll = () => setSelected(new Set());

  const run = async (action) => {
    if (selected.size === 0) { toast.error("Select at least one tenant"); return; }
    if (action === "delete" && !window.confirm(`Hard-delete ${selected.size} tenant(s) AND all owned data? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/tenants/bulk`, {
        tenant_ids: Array.from(selected),
        action,
      });
      const skip = data.skipped_count ? ` — ${data.skipped_count} skipped (keeper)` : "";
      toast.success(`${data.affected_count} tenant(s) ${data.action}d${skip}`);
      setSelected(new Set());
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="bulk-subscriber-actions">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" />
          Bulk Subscriber Actions
          <InfoBubble
            title="Mass actions"
            body="Suspend, reactivate, or hard-delete multiple tenants in one call. The keeper account is auto-protected and silently skipped."
            testid="bulk-info"
          />
        </CardTitle>
        <CardDescription>Select rows below and apply an action. {selected.size > 0 && <strong>{selected.size} selected</strong>}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={selectAll} data-testid="bulk-select-all">Select all</Button>
          <Button variant="outline" size="sm" onClick={clearAll} data-testid="bulk-clear">Clear</Button>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" disabled={busy || selected.size === 0} onClick={() => run("suspend")} data-testid="bulk-suspend-btn">
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Pause className="h-3.5 w-3.5 mr-1" />} Suspend
            </Button>
            <Button variant="outline" size="sm" disabled={busy || selected.size === 0} onClick={() => run("reactivate")} data-testid="bulk-reactivate-btn">
              <Play className="h-3.5 w-3.5 mr-1" /> Reactivate
            </Button>
            <Button variant="destructive" size="sm" disabled={busy || selected.size === 0} onClick={() => run("delete")} data-testid="bulk-delete-btn">
              <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
            </Button>
          </div>
        </div>
        {tenants.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No tenants to act on.</p>
        ) : (
          <div className="rounded-md border max-h-80 overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase bg-muted/50">
                <tr><th className="p-2 w-8"></th><th className="text-left p-2">Name</th><th className="text-left p-2">Status</th><th className="text-left p-2">Plan</th></tr>
              </thead>
              <tbody>
                {tenants.map(t => (
                  <tr key={t.id} className="border-t hover:bg-muted/30" data-testid={`bulk-row-${t.id}`}>
                    <td className="p-2"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggle(t.id)} data-testid={`bulk-check-${t.id}`} /></td>
                    <td className="p-2 font-medium">{t.name}</td>
                    <td className="p-2"><Badge variant={t.is_active ? "default" : "secondary"}>{t.subscription_status || (t.is_active ? "active" : "suspended")}</Badge></td>
                    <td className="p-2 text-xs text-muted-foreground">{t.subscription_plan || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
